import * as React from 'react';
import { FC } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import Image from './Image';
import stylesheet, { bodyFontSize, backgroundColor } from 'src/constants/stylesheet';
import formatDate from 'src/utils/FormatDate';
import mediaQueries from 'src/constants/responsive';
import * as classNames from 'classnames';
import { Link } from 'react-router-dom';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            position: 'relative',
            width: '100%',
            height: '100%',
        },
        hr: {
            ...stylesheet.HR
        },
        figure: {
            position: 'relative',
            height: 0,
            paddingBottom: '50%',
            overflow: 'hidden',
            marginBottom: 5,
            backgroundColor,
        },
        image: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            filter: 'grayscale(1)',
            transform: 'scale(1) translateZ(0)',
            transition: 'all .4s ease',

            '$figure:hover &': {
                transform: 'scale(1.1) translateZ(0)',
                filter: 'grayscale(0)',
            }
        },
        coverImage: {
            objectFit: 'cover'
        },
        containImage: {
            objectFit: 'contain',
            padding: 15
        },
        text: {
            position: 'relative',
            height: 160,
            overflow: 'hidden',
            zIndex: 0,

            '&:after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 1,
                height: 100,
                background: 'linear-gradient(0deg,#fff 7%,hsla(0,0%,100%,0))',
            },
        },
        title: {
            position: 'relative',
            ...stylesheet.TitleH3
        },
        date: {
            ...stylesheet.Condensed,
            color: 'black',
            [mediaQueries.Mobile]: {
                fontSize: `${12 / bodyFontSize.Mobile}rem`
            },
            [mediaQueries.Tablet]: {
                fontSize: `${12 / bodyFontSize.Tablet}rem`
            },
            [mediaQueries.Laptop]: {
                fontSize: `${12 / bodyFontSize.Laptop}rem`
            },
            [mediaQueries.Desktop]: {
                fontSize: `${12 / bodyFontSize.Desktop}rem`
            }
        },
        previewText: {},
        link: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        },
        tagsContainer: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 5,

            display: 'flex',
            flexWrap: 'wrap',
            maring: '0 -2px',
        },
        tag: {
            ...stylesheet.Tag,
            margin: '0 2px 4px'
        },
    }));

export type PreviewItemsProps = {
    previewItem: IDto;
};
type Props = PreviewItemsProps;

const PreviewItem: FC<Props> = ({ previewItem }) => {
    const cls = useStyles();

    const sizes = '(max-width:767px) 100vw, ' + 
                    '(min-width: 768px) and (max-width: 1024) and (orientation: landscape) calc(100vw / 3), ' +
                    '(min-width: 768px) and (max-width: 1024) and (orientation: portrait) calc(100vw / 2), ' +
                    '(min-width: 1025px) 300px';

    switch (previewItem.type) {
        case "Preview":
            // case "NewsPreview":
            const newsPreview = previewItem as PreviewDto;
            const isInternalLink = newsPreview?.Link.startsWith('/');
            return (
                <div
                    className={cls.root}
                >
                    <hr className={cls.hr} />
                    <figure className={cls.figure}>
                        <Image
                            pictureRef={newsPreview.PreviewImage}
                            sizes={sizes}
                            className={
                                classNames(
                                    cls.image,
                                    newsPreview.isCover ? cls.coverImage : cls.containImage)
                            }
                        />
                        {newsPreview.Link && !isInternalLink &&
                            <a
                                href={newsPreview.Link}
                                target='_blank'
                                className={cls.link}
                            />}
                        {newsPreview.Link && isInternalLink &&
                            <Link
                                to={newsPreview.Link}
                                className={cls.link}
                            />}
                    </figure>
                    <div className={cls.text}>
                        <h5 className={cls.title}>
                            {newsPreview.Title}
                            {newsPreview.Link && !isInternalLink &&
                                <a
                                    href={newsPreview.Link}
                                    target='_blank'
                                    className={cls.link}
                                />}
                            {newsPreview.Link && isInternalLink &&
                                <Link
                                    to={newsPreview.Link}
                                    className={cls.link}
                                />}
                        </h5>
                        {newsPreview.Date &&
                            <time
                                className={cls.date}
                                dateTime={formatDate(newsPreview.Date)}
                            >
                                {formatDate(newsPreview.Date)}
                            </time>
                        }
                        <div
                            className={cls.previewText}
                            dangerouslySetInnerHTML={{ __html: newsPreview.PreviewText }} />
                        {
                            // newsPreview.Tags &&
                            // <div className={cls.tagsContainer}>
                            //     {renderTags(newsPreview.Tags)}
                            // </div>
                        }
                    </div>
                </div>);

        case "Product":
            const product = previewItem as ProductPreviewDto;
            return (
                <div
                    className={cls.root}
                >
                    <hr className={cls.hr} />
                    <figure className={cls.figure}>
                        <Image
                            pictureRef={product.PreviewImage}
                            sizes={sizes}
                            className={
                                classNames(
                                    cls.image,
                                    product.isCover ? cls.coverImage : cls.containImage)
                            }
                        />
                        {product.Link && <Link to={product.Link} className={cls.link} />}
                    </figure>
                    <div className={cls.text}>
                        <h5 className={cls.title}>
                            {product.Title}
                            {product.Link && <Link to={product.Link} className={cls.link} />}
                        </h5>
                        <div
                            className={cls.previewText}
                            dangerouslySetInnerHTML={{ __html: product.PreviewText }} />
                    </div>
                </div>);
        case "LoosePage":
            const loosePage = previewItem as LoosePagePreviewDto;
            return (
                <div
                    className={cls.root}
                >
                    <hr className={cls.hr} />
                    <figure className={cls.figure}>
                        <Image
                            pictureRef={loosePage.PreviewImage}
                            sizes={sizes}
                            className={
                                classNames(
                                    cls.image,
                                    loosePage.isCover ? cls.coverImage : cls.containImage)
                            }
                        />
                        {loosePage.Link && <Link to={loosePage.Link} className={cls.link} />}
                    </figure>
                    <div className={cls.text}>
                        <h5 className={cls.title}>
                            {loosePage.Title}
                            {loosePage.Link && <Link to={loosePage.Link} className={cls.link} />}
                        </h5>
                        <div
                            className={cls.previewText}
                            dangerouslySetInnerHTML={{ __html: loosePage.PreviewText }} />
                    </div>
                </div>);

        case "DownloadItem":
            const downloadItem = previewItem as DownloadItemDto;
            return (
                <div
                    className={cls.root}
                >
                    <hr className={cls.hr} />
                    <figure className={cls.figure}>
                        <Image
                            pictureRef={downloadItem.PreviewImage}
                            sizes={sizes}
                            className={
                                classNames(
                                    cls.image,
                                    downloadItem.isCover ? cls.coverImage : cls.containImage)
                            }
                        />
                        {downloadItem.Link &&
                            <a
                                href={`${downloadItem.Link}`}
                                className={cls.link}
                                target={'_blank'}
                            />
                        }
                        {!downloadItem.Link && downloadItem.File &&
                            <a href={`${downloadItem.File.Src}`} className={cls.link} target={'_blank'} />
                        }
                    </figure>
                    <div className={cls.text}>
                        <h5 className={cls.title}>
                            {downloadItem.Title}
                            {downloadItem.Link &&
                                <a
                                    href={`${downloadItem.Link}`}
                                    className={cls.link}
                                    target={'_blank'}
                                />
                            }
                            {!downloadItem.Link && downloadItem.File &&
                                <a href={`${downloadItem.File.Src}`} className={cls.link} target={'_blank'} />
                            }
                        </h5>
                        <div
                            className={cls.previewText}
                            dangerouslySetInnerHTML={{ __html: downloadItem.PreviewText }} />
                    </div>
                </div>);

        default:
            return null;
    };
}

export default PreviewItem;


// WEBPACK FOOTER //
// ./src/Components/PreviewItem.tsx